import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ArrowDown, ArrowUp, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

export const MIN_NOTIONAL = 10;
export const MAX_NOTIONAL = 100;

const initial = {
  counter_exchange: "BINANCE",
  size_usd: 25,  // USDT input (test phase default)
  leverage: 5,
  entry_price_darkex: "",
  entry_price_counter: "",
  bonus_rate: 10,
  darkex_fee_rate: 0.05,
  counter_fee_rate: 0.05,
  darkex_margin_mode: "ISOLATED",
  counter_margin_mode: "ISOLATED",
  notes: "",
};

export const NewPositionDialog = ({ defaultPrices, defaults }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const qc = useQueryClient();

  useEffect(() => {
    if (open && defaults) {
      setForm((f) => ({
        ...f,
        counter_exchange: defaults.preferred_counter_exchange || f.counter_exchange,
        leverage: defaults.default_leverage ?? f.leverage,
        bonus_rate: defaults.default_bonus_rate ? defaults.default_bonus_rate * 100 : f.bonus_rate,
        darkex_fee_rate: defaults.default_darkex_fee_rate
          ? defaults.default_darkex_fee_rate * 100
          : f.darkex_fee_rate,
        counter_fee_rate: defaults.default_counter_fee_rate
          ? defaults.default_counter_fee_rate * 100
          : f.counter_fee_rate,
      }));
    }
  }, [open, defaults]);

  useEffect(() => {
    if (open && defaultPrices) {
      const counter = form.counter_exchange.toLowerCase();
      setForm((f) => ({
        ...f,
        entry_price_darkex:
          f.entry_price_darkex || defaultPrices.darkex?.price?.toFixed(2) || "",
        entry_price_counter:
          f.entry_price_counter ||
          (defaultPrices[counter]?.price?.toFixed(2) ?? ""),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultPrices, form.counter_exchange]);

  const notional = useMemo(() => {
    const usd = parseFloat(form.size_usd);
    return isNaN(usd) ? 0 : usd;
  }, [form.size_usd]);

  const sizeEth = useMemo(() => {
    const a = parseFloat(form.entry_price_darkex);
    const b = parseFloat(form.entry_price_counter);
    const usd = parseFloat(form.size_usd);
    if (!usd) return 0;
    const avg = (a && b) ? (a + b) / 2 : (a || b);
    return avg ? usd / avg : 0;
  }, [form.size_usd, form.entry_price_darkex, form.entry_price_counter]);

  const notionalError =
    notional > 0 && notional < MIN_NOTIONAL
      ? `Minimum işlem tutarı $${MIN_NOTIONAL}. Şu an $${notional.toFixed(2)}.`
      : notional > MAX_NOTIONAL
      ? `Maksimum işlem tutarı $${MAX_NOTIONAL.toLocaleString()}. Şu an $${notional.toLocaleString(undefined, { maximumFractionDigits: 2 })}.`
      : "";

  const mutation = useMutation({
    mutationFn: api.createPosition,
    onSuccess: () => {
      toast.success("Hedge çifti açıldı");
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setOpen(false);
      setForm(initial);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      // Daily loss limit: detail = {code, message, today_net, limit}
      if (detail && typeof detail === "object" && detail.code === "DAILY_LOSS_LIMIT_HIT") {
        toast.error(detail.message, { duration: 10000 });
        qc.invalidateQueries({ queryKey: ["daily-pnl"] });
        return;
      }
      const msg = (typeof detail === "string" ? detail : detail?.message)
        || e?.message || String(e);
      toast.error(`Açılamadı: ${msg}`);
    },
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      counter_exchange: form.counter_exchange,
      size_eth: parseFloat(sizeEth.toFixed(6)),
      leverage: parseFloat(form.leverage),
      entry_price_darkex: parseFloat(form.entry_price_darkex),
      entry_price_counter: parseFloat(form.entry_price_counter),
      bonus_rate: parseFloat(form.bonus_rate) / 100,
      darkex_fee_rate: parseFloat(form.darkex_fee_rate) / 100,
      counter_fee_rate: parseFloat(form.counter_fee_rate) / 100,
      darkex_margin_mode: form.darkex_margin_mode,
      counter_margin_mode: form.counter_margin_mode,
      notes: form.notes || undefined,
    };
    if (!payload.size_eth || !payload.entry_price_darkex || !payload.entry_price_counter) {
      toast.error("Tutar ve iki giriş fiyatı da zorunlu");
      return;
    }
    if (notionalError) {
      toast.error(notionalError);
      return;
    }
    mutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="open-new-position-btn"
          className="bg-[#FF9F0A] hover:bg-[#FFB340] text-black rounded-sm font-semibold h-9 px-3"
        >
          <Plus className="w-4 h-4 mr-1" /> Hedge Çifti Aç
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-2xl rounded-md">
        <DialogHeader>
          <DialogTitle className="font-bold tracking-tight">
            ETH Hedge Çifti Aç
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Darkex&apos;te SHORT · Karşı borsada LONG (aynı fiyattan).
            Darkex kaybında %35 bonus kapanışta otomatik eklenir.
          </p>
          <p className="text-[10px] text-zinc-600">
            Test fazı limiti: ${MIN_NOTIONAL} – ${MAX_NOTIONAL} USDT
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field label="Karşı Borsa">
            <Select
              value={form.counter_exchange}
              onValueChange={(v) => setForm({ ...form, counter_exchange: v })}
            >
              <SelectTrigger
                data-testid="form-counter-exchange"
                className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] text-white rounded-sm">
                <SelectItem value="OKX">OKX</SelectItem>
                <SelectItem value="BINANCE">Binance</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Tutar (USDT)">
            <Input
              data-testid="form-size-usd"
              type="number"
              step="50"
              value={form.size_usd}
              onChange={(e) => setForm({ ...form, size_usd: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm font-mono"
            />
            {sizeEth > 0 && (
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                ≈ {sizeEth.toFixed(6)} adet (giriş fiyatından)
              </div>
            )}
          </Field>

          <Field
            label={
              <span className="flex items-center gap-1">
                <ArrowDown className="w-3 h-3 text-[#FF3B30]" />
                Darkex Giriş · SHORT
              </span>
            }
          >
            <Input
              data-testid="form-entry-darkex"
              type="number"
              step="0.01"
              value={form.entry_price_darkex}
              onChange={(e) =>
                setForm({ ...form, entry_price_darkex: e.target.value })
              }
              className="bg-[#0A0A0A] border-[#FF3B30]/30 h-9 rounded-sm font-mono"
            />
          </Field>

          <Field
            label={
              <span className="flex items-center gap-1">
                <ArrowUp className="w-3 h-3 text-[#34C759]" />
                Karşı Giriş · LONG
              </span>
            }
          >
            <Input
              data-testid="form-entry-counter"
              type="number"
              step="0.01"
              value={form.entry_price_counter}
              onChange={(e) =>
                setForm({ ...form, entry_price_counter: e.target.value })
              }
              className="bg-[#0A0A0A] border-[#34C759]/30 h-9 rounded-sm font-mono"
            />
          </Field>

          <Field label="Kaldıraç (x)">
            <Input
              data-testid="form-leverage"
              type="number"
              step="0.1"
              value={form.leverage}
              onChange={(e) => setForm({ ...form, leverage: e.target.value })}
              className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm font-mono"
            />
          </Field>

          <Field label="Bonus Oranı (%)">
            <Input
              data-testid="form-bonus-rate"
              type="number"
              step="0.1"
              value={form.bonus_rate}
              onChange={(e) => setForm({ ...form, bonus_rate: e.target.value })}
              className="bg-[#0A0A0A] border-[#FF9F0A]/30 h-9 rounded-sm font-mono"
            />
          </Field>

          <Field label="Darkex Komisyon (%)">
            <Input
              data-testid="form-darkex-fee"
              type="number"
              step="0.001"
              value={form.darkex_fee_rate}
              onChange={(e) =>
                setForm({ ...form, darkex_fee_rate: e.target.value })
              }
              className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm font-mono"
            />
          </Field>

          <Field label="Karşı Borsa Komisyon (%)">
            <Input
              data-testid="form-counter-fee"
              type="number"
              step="0.001"
              value={form.counter_fee_rate}
              onChange={(e) =>
                setForm({ ...form, counter_fee_rate: e.target.value })
              }
              className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm font-mono"
            />
          </Field>

          <Field label="Darkex Teminat Modu">
            <Select
              value={form.darkex_margin_mode}
              onValueChange={(v) => setForm({ ...form, darkex_margin_mode: v })}
            >
              <SelectTrigger
                data-testid="form-darkex-margin-mode"
                className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] text-white rounded-sm">
                <SelectItem value="ISOLATED">İzole (önerilen)</SelectItem>
                <SelectItem value="CROSS">Cross (tüm cüzdan riski)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Karşı Borsa Teminat Modu">
            <Select
              value={form.counter_margin_mode}
              onValueChange={(v) => setForm({ ...form, counter_margin_mode: v })}
            >
              <SelectTrigger
                data-testid="form-counter-margin-mode"
                className="bg-[#0A0A0A] border-[#27272A] h-9 rounded-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] text-white rounded-sm">
                <SelectItem value="ISOLATED">İzole (önerilen)</SelectItem>
                <SelectItem value="CROSS">Cross (tüm cüzdan riski)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <div className="col-span-2">
            <Field label="Notlar (opsiyonel)">
              <Textarea
                data-testid="form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="İşlem tezi, hedef fark, çıkış planı…"
                className="bg-[#0A0A0A] border-[#27272A] rounded-sm min-h-[60px]"
              />
            </Field>
          </div>

          {/* Notional indicator */}
          <div
            data-testid="notional-summary"
            className={`col-span-2 flex items-center justify-between px-3 py-2 rounded-sm border text-xs ${
              notionalError
                ? "border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] text-[#FF3B30]"
                : "border-[#27272A] bg-[#0A0A0A] text-zinc-400"
            }`}
          >
            <span className="uppercase tracking-wider text-[10px]">
              Tahmini İşlem Tutarı (Nominal)
            </span>
            <span className="font-mono">
              {notional > 0 ? `$${notional.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
            </span>
          </div>
          {notionalError && (
            <div
              data-testid="notional-error"
              className="col-span-2 flex items-start gap-1.5 text-[11px] text-[#FF3B30]"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {notionalError}
            </div>
          )}

          <DialogFooter className="col-span-2 gap-2 mt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:bg-[#1A1A1A] hover:text-white rounded-sm h-9"
            >
              Vazgeç
            </Button>
            <Button
              data-testid="submit-position-btn"
              type="submit"
              disabled={mutation.isPending || !!notionalError}
              className="bg-[#FF9F0A] hover:bg-[#FFB340] text-black rounded-sm font-semibold h-9 px-4 disabled:opacity-40"
            >
              {mutation.isPending ? "Açılıyor…" : "Onayla & Çifti Aç"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
      {label}
    </Label>
    {children}
  </div>
);
